import { createEffect, untrack } from "solid-js"
import { Group, type Object3D } from "three"
import diagonalDirections from "../../core/direction/diagonalDirections"
import type { Engine } from "../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import applyEntityRotationToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityRotationToNextObject3DFrame"
import applyEntityWalkPathToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityWalkPathToNextObject3DFrame"
import type { LightReconEntity } from "../../core/entity/EntityType/LightRecon"
import shouldEntityShowFadedFrames from "../../core/entity/shouldEntityShowFadedFrames"
import tmpRng from "../../core/rng/tmpRng"
import { PI } from "../../core/util/math/Math"
import { mountOnFrameUpdateToObject3D } from "../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import assertClassInDev from "../../ldsh/assertClassInDev"
import { LightRecon1_LightRecon1_GLTF_Model } from "../gltf_models.generated"
import { appGltfLoader } from "./appGltfLoader"
import applyPlayerColors from "./applyPlayerColors"
import cubicEaseInOutBounce from "./cubicEaseInOutBounce"
import makeLightReconWheelScene from "./makeLightReconWheelScene"
import runWithObject3DOwner from "./runWithObject3DOwner"

const totalLoopMs = 1000

// const heights = [1, 2, 2, 3, 3, 4]

export default function makeLightReconScene(
  engine: Engine,
  ent: EntityGraphNode<LightReconEntity>,
  callback: (newGroup: Object3D) => void
): void {
  return appGltfLoader.load(LightRecon1_LightRecon1_GLTF_Model, async function (ModelObj) {

    tmpRng.seed = ent.id
    const { scene } = ModelObj

    const bodyGroup = scene.getObjectByName('Body') as Group
    assertClassInDev(bodyGroup, Group)

    const wheelScaleX = 2.4
    const wheelFrontOffsetY = 5.5
    const wheelBackOffsetY = 5

    const wheelRH = 0.2

    await new Promise<void>(resolve => makeLightReconWheelScene(engine, ent, (wheelSceneBase) => {
      diagonalDirections.forEach((direction) => {
        const wheelScene = wheelSceneBase.clone()
        const isFrontWheel = direction.dy > 0
        const isLeftWheel = direction.dx < 0
        wheelScene.scale.divideScalar(1.8)

        if (isLeftWheel) {
          wheelScene.rotation.y = PI
        }
        const wheelPosition = wheelScene.position

        wheelPosition.set(wheelScaleX * direction.dx, wheelRH, (isFrontWheel ? wheelFrontOffsetY : wheelBackOffsetY) * direction.dy)

        scene.add(wheelScene)
      })

      resolve()
    }))

    const defaultHullY = bodyGroup.position.y
    // const defaultHullX = 0

    let entValue = untrack(ent.raw)
    runWithObject3DOwner(bodyGroup, () => {
      createEffect(() => {
        entValue = ent.raw()
      })
    })

    mountOnFrameUpdateToObject3D(engine, bodyGroup, (elapsedMs: number, frameAt: number): void => {
      const mod = frameAt % totalLoopMs
      if (shouldEntityShowFadedFrames(entValue)) {
        bodyGroup.position.y = defaultHullY
      } else if (entValue.player_id) {
        bodyGroup.position.y = defaultHullY + (cubicEaseInOutBounce(mod / 500) / 7)
      }
      applyEntityRotationToNextObject3DFrame(scene, elapsedMs)
      applyEntityWalkPathToNextObject3DFrame(scene, elapsedMs)
    })

    applyPlayerColors(engine, scene, ent)

    callback(scene)
  })
}
